import { ErrorLevel, ValidationResult } from "./Interfaces";
import { AircraftExtended, TicketExtended } from "./TypeExtensions";
import { FilterLineItems, IsEngineerRequired } from "./UtilityMethods";
import dayjs from "dayjs";
import FuelCalculator from "./FuelCalculator";
import { Aircraft, TicketLocation } from "../sharedCode/avroGraphql";
import { CUSTOMER_SIGNATURE } from "../Sync/useSignableResourceTypeBackgroundPull";
import TimeCalculator, { UpDownTimeOnEachLeg } from "../sharedCode/TimeCalculator";
import { DefinitelyAnArray, getLastRevision, HasValue, isInvalidTimeString, isValidTimeString, sortFlightLegsByTime } from "../sharedCode/HelperMethods";
import {
    DUMMY_CHARGE_LINE_ITEM_TYPE,
    DUMMY_FUEL_LINE_ITEM_TYPE,
    FUEL_LINE_ITEM_TYPES,
    LANDING_LINE_ITEM_TYPE,
    LANDING_LINE_ITEM_TYPES,
    OTHER_CHARGES_LINE_ITEM_TYPES,
    PER_DIEMS_LINE_ITEM_TYPES,
    REVISIONS_ALLOWED_DAYS_BACK,
} from "../sharedCode/metadata";
import { findById } from "./ContextLookups";
import { getDescriptiveName, TicketErrorHelper } from "./TicketErrorHelper";

// TODO this seems kind of ugly, we need a better home for these (they aren't initialized soon enough on TicketPage)
export const SHOW_MENU = "SHOW_MENU";
export const CUSTOMER = "CLIENT AND JOB";
export const CREW = "CREW AND AIRCRAFT";
export const PASSENGERS = "PASSENGERS";
export const FLIGHT_LEGS = "FLIGHT LEGS";
export const CHARGES = "CHARGES";
export const PER_DIEMS = "PER DIEMS";
export const FUEL = "FUEL";
export const LANDINGS = "LANDINGS";
export const NOTES = "NOTES";
export const MINIMUMS = "MINIMUMS";
export const ITINERARY_PDF = "ITINERARY";
export const RISK_ASSESSMENT_PDF = "RISK ASSESSMENT";
export const SAFETY_BRIEFING_PDF = "SAFETY BRIEFINGS";
export const TRIP_TICKET_PDF = "PDF";
export const TICKET_NAVIGATOR = "TICKET_NAVIGATOR";

export const FAILED_SYNC = 1;
export const DUPLICATE_TICKET = 2;
export const PARTIAL_SYNC = 3;
export const FAILED_STALE = 4;

export default class TicketValidator {
    private static fieldMetadata = {
        Ticket: {
            ticket_num: {
                editCard: TICKET_NAVIGATOR,
                friendlyName: "TICKET NUMBER",
            },
            date: {
                editCard: "ticket/customer",
                friendlyName: "DATE",
            },
            company_id: {
                editCard: "ticket/customer",
                friendlyName: "COMPANY",
            },
            job_id: {
                editCard: "ticket/customer",
                friendlyName: "JOB",
            },
            contact_email: {
                editCard: "ticket/customer",
                friendlyName: "EMERGENCY CONTACT EMAIL",
            },
            pilot_person_id: {
                editCard: "ticket/crew",
                friendlyName: "PILOT",
            },
            pilot_person_id2: {
                editCard: "ticket/crew",
                friendlyName: "SECOND PILOT",
            },
            ame_person_id: {
                editCard: "ticket/crew",
                friendlyName: "ENGINEER",
            },
            airport_code: {
                editCard: "ticket/crew",
                friendlyName: "BASE LOCATION",
            },
            job_location: {
                editCard: "ticket/crew",
                friendlyName: "JOB LOCATION",
            },
            aircraft_id: {
                editCard: "ticket/crew",
                friendlyName: "AIRCRAFT",
            },
            is_aircraft_substituted: {
                editCard: "ticket/crew",
                friendlyName: "A/C SUBSTITUTED",
            },
            substituted_aircraft_id: {
                editCard: "ticket/crew",
                friendlyName: "REQUESTED AIRCRAFT",
            },
            up_time: {
                editCard: "ticket/legs",
                friendlyName: "UP TIME",
            },
            down_time: {
                editCard: "ticket/legs",
                friendlyName: "DOWN TIME",
            },
            is_no_fly: {
                editCard: "ticket/customer",
                friendlyName: "FLYING TODAY ANSWER",
            },
            is_per_diems: {
                editCard: "ticket/customer",
                friendlyName: "PER DIEMS ANSWER",
            },
            is_other_charges: {
                editCard: "ticket/customer",
                friendlyName: "ADDITIONAL CHARGES ANSWER",
            },
            any_landings: {
                editCard: "ticket/customer",
                friendlyName: "AIRPORT LANDINGS ANSWER",
            },
        },
        TicketLocation: {
            location: {
                editCard: "ticket/legs",
                friendlyName: "LOCATION",
            },
            up_time: {
                editCard: "ticket/legs",
                friendlyName: "UP TIME",
            },
            down_time: {
                editCard: "ticket/legs",
                friendlyName: "DOWN TIME",
            },
            hours: {
                editCard: "ticket/legs",
                friendlyName: "HOURS",
            },
        },
        TicketPax: {
            name: {
                editCard: "ticket/pax",
                friendlyName: "PASSENGER NAME",
            },
        },
        "ticket/charges": {
            line_item_type_id: {
                editCard: "ticket/charges",
                friendlyName: "CHARGE TYPE",
            },
        },
        "ticket/fuel": {
            line_item_type_id: {
                editCard: "ticket/fuel",
                friendlyName: "SOURCE",
            },
            amount: {
                editCard: "ticket/fuel",
                friendlyName: "LITRES",
            },
        },
        LANDINGS: {
            comments: {
                editCard: "ticket/landings",
                friendlyName: "AIRPORT NAME AND CODE",
            },
            quantity: {
                editCard: "ticket/landings",
                friendlyName: "COUNT",
            },
        },
    };

    static validate(aircraftList: AircraftExtended[], ticketList: TicketExtended[], ticket: TicketExtended): ValidationResult[] {
        const errors = [] as ValidationResult[];
        const aircraft = findById(aircraftList, ticket.aircraft_id);

        if (this.ShouldValidate(ticketList, ticket)) {
            this.ValidateTicket(ticket, errors, aircraft);
            this.ValidatePax(ticket, errors);
            this.ValidateFlightLegs(ticket, errors);
            this.ValidateFuel(ticket, errors, aircraft);
            this.ValidateLineItems(ticket, errors);
        }
        return errors;
    }

    static IsReadOnly(ticket: TicketExtended): boolean {
        const isStale = this.IsStale(ticket);
        const isComplete = this.IsComplete(ticket);
        const isAtLeastPartiallySynced = ticket.id > 0;
        const isStalledSyncStatus = this.IsStalledSyncStatus(ticket);
        // Stale forces read-only when: synced, complete, or original stalled ticket (FAILED_STALE/PARTIAL_SYNC).
        // Only a new revision (stale, local, incomplete, pushAttempts reset to 0) stays editable.
        return isAtLeastPartiallySynced || isComplete || (isStale && (isAtLeastPartiallySynced || isComplete || isStalledSyncStatus));
    }

    static IsComplete(ticket: TicketExtended): boolean {
        const isSigned = DefinitelyAnArray(ticket.Signatures).findIndex((s) => s.signable_resource_type_id === CUSTOMER_SIGNATURE) >= 0;
        const isUnsignedButComplete = (ticket.signature_pending ?? 0) > 0;
        return isSigned || isUnsignedButComplete;
    }

    static IsStale(ticket: TicketExtended): boolean {
        return !this.IsWithinRevisionRange(ticket);
    }

    static ShouldShowDataDisclaimer(ticket: TicketExtended, validationMessages: ValidationResult[]): boolean {
        // Always show for stalled sync (FAILED_STALE/PARTIAL_SYNC) so the original ticket still shows disclaimer after a revision is pushed
        if (this.IsStalledSyncStatus(ticket)) return true;
        return this.IsReadOnly(ticket) && (TicketErrorHelper.containsErrors(validationMessages) || !TicketValidator.IsComplete(ticket));
    }

    static IsWithinRevisionRange(ticket: TicketExtended): boolean {
        // as per discussion with Paul 2023/09/11
        return dayjs().diff(dayjs(ticket.date), "days") <= REVISIONS_ALLOWED_DAYS_BACK;
    }

    /** True for tickets that had a stalled sync (FAILED_STALE or PARTIAL_SYNC); these can view/email PDF and revise regardless of age. */
    static IsStalledSyncStatus(ticket: TicketExtended): boolean {
        return ticket.pushAttempts === FAILED_STALE || ticket.pushAttempts === PARTIAL_SYNC;
    }

    static CanRevise(ticketList: TicketExtended[], ticket: TicketExtended): boolean {
        if (!this.IsReadOnly(ticket)) return false;

        const lastRevision = getLastRevision(ticketList, ticket);
        if (ticket.revision_num != lastRevision?.revision_num) return false;

        // Allow revision regardless of age for stalled sync statuses
        if (this.IsStalledSyncStatus(ticket)) return true;
        return this.IsWithinRevisionRange(ticket);
    }

    static ShouldValidate(ticketList: TicketExtended[], ticket: TicketExtended): boolean {
        return !this.IsReadOnly(ticket) || this.CanRevise(ticketList, ticket);
    }

    private static getFriendlyName(entityName: string, fieldName: string): string {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const entityMeta = this.fieldMetadata[entityName];
        if (!entityMeta) return fieldName;

        const fieldMeta = entityMeta[fieldName];
        if (!fieldMeta) return fieldName;

        return fieldMeta.friendlyName;
    }

    private static getEditCard(entityName: string, fieldName: string): string {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const entityMeta = this.fieldMetadata[entityName];
        if (!entityMeta) return entityName;

        const fieldMeta = entityMeta[fieldName];
        if (!fieldMeta) return entityName;

        return fieldMeta.editCard;
    }

    // TODO - convert this to a function component so it can instantiate its own aircraft list
    private static ValidateTicket(ticket: TicketExtended, errors: ValidationResult[], aircraft: Aircraft | undefined) {
        // check for required values
        // TODO this should be feeding to the form props too
        const requiredFields = [
            "ticket_num",
            "date",
            "company_id",
            "job_id",
            "is_no_fly",
            "is_per_diems",
            "is_other_charges",
            "any_landings",
            "pilot_person_id",
            "airport_code",
            "aircraft_id",
            "is_aircraft_substituted",
        ];

        if (HasValue(ticket.is_no_fly) && !ticket.is_no_fly && !(ticket.up_down_required !== 0)) {
            requiredFields.push("up_time");
            requiredFields.push("down_time");
            this.validateFirstUpLastDown(ticket, errors);
        }

        this.validateRequiredFields(ticket, "Ticket", requiredFields, errors);

        if (HasValue(ticket.is_no_fly) && ticket.is_no_fly && HasValue(ticket.any_landings) && ticket.any_landings) {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: ticket.id,
                entityName: "Ticket",
                propertyName: "any_landings",
                editCard: "ticket/customer",
                message: `LANDINGS NOT ALLOWED`,
                level: ErrorLevel.Error,
            });
        }

        if (ticket.pushAttempts === FAILED_SYNC || ticket.pushAttempts === DUPLICATE_TICKET) {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: ticket.id,
                entityName: "Ticket",
                propertyName: "ticket_num",
                editCard: "ticket/pdf",
                message: `SYNC INCOMPLETE, CLICK REFRESH TO TRY AGAIN`,
                level: ErrorLevel.Warn,
            });
        }

        if (ticket.pushAttempts === PARTIAL_SYNC) {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: ticket.id,
                entityName: "Ticket",
                propertyName: "ticket_num",
                editCard: "ticket/pdf",
                message: `SYNC ISSUE, TICKET INCOMPLETE`,
                level: ErrorLevel.Warn,
            });
        }

        if (ticket.pilot_person_id && ticket.pilot_person_id2 && ticket.pilot_person_id === ticket.pilot_person_id2) {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: ticket.id,
                entityName: "Ticket",
                propertyName: "pilot_person_id2",
                editCard: this.getEditCard("Ticket", "pilot_person_id2"),
                message: `DUPLICATE PILOT`,
                level: ErrorLevel.Error,
            });
        }

        if (IsEngineerRequired(aircraft) && !HasValue(ticket.ame_person_id)) {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: ticket.id,
                entityName: "Ticket",
                propertyName: "ame_person_id",
                editCard: this.getEditCard("Ticket", "ame_person_id"),
                message: `ENGINEER REQUIRED FOR ${aircraft?.AircraftType?.name}`,
                level: ErrorLevel.Error,
            });
        }

        if (ticket.is_aircraft_substituted && !HasValue(ticket.substituted_aircraft_id)) {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: ticket.id,
                entityName: "Ticket",
                propertyName: "substituted_aircraft_id",
                editCard: this.getEditCard("Ticket", "substituted_aircraft_id"),
                message: `SUBSTITUTED AIRCRAFT REQUIRED`,
                level: ErrorLevel.Error,
            });
        }
    }

    private static validateRequiredFieldsArray<T>(entities: Record<string, unknown>[], entityName: string, requiredFields: string[], errors: ValidationResult[]) {
        for (let i = 0; i < entities.length; i++) {
            this.validateRequiredFields(entities[i], entityName, requiredFields, errors);
        }
    }

    private static validateRequiredFields<T>(entity: Record<string, unknown>, entityName: string, requiredFields: string[], errors: ValidationResult[]) {
        const workingFields = requiredFields.slice();
        for (const [fieldName, fieldValue] of Object.entries(entity)) {
            const requiredIndex = workingFields.indexOf(fieldName);
            if (requiredIndex >= 0) {
                if (fieldValue == null || fieldValue === "") {
                    errors.push({
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        entityId: entity.id,
                        entityName: entityName,
                        propertyName: fieldName,
                        editCard: this.getEditCard(entityName, fieldName),
                        message: `${this.getFriendlyName(entityName, fieldName)} REQUIRED`,
                        level: ErrorLevel.Error,
                    });
                }
                workingFields.splice(requiredIndex, 1);
            }
        }

        // any fields left in the array were not found on the object and are therefore undefined so we can just add errors for everything still in the array
        workingFields.forEach((field) => {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: entity.id,
                entityName: entityName,
                propertyName: field,
                editCard: this.getEditCard(entityName, field),
                message: `${this.getFriendlyName(entityName, field)} REQUIRED`,
                level: ErrorLevel.Error,
            });
        });
    }

    private static ValidatePax(ticket: TicketExtended, errors: ValidationResult[]) {
        // TODO have to be able to identify the pax row with the error (by id?) in the errors object

        const requiredFields = ["name"];
        const paxToValidate = DefinitelyAnArray(ticket.Pax);
        this.validateRequiredFieldsArray(paxToValidate, "TicketPax", requiredFields, errors);

        // starting at the second name just check that the index for each name is its own index (otherwise it's found a dup in the list ahead of it)
        const passengerName = this.getFriendlyName("TicketPax", "name");
        for (let i = 1; i < paxToValidate.length; i++) {
            if (HasValue(paxToValidate[i].name) && i != paxToValidate.findIndex((p) => p.name === paxToValidate[i].name)) {
                errors.push({
                    entityId: paxToValidate[i].id,
                    entityName: "TicketPax",
                    propertyName: "name",
                    editCard: this.getEditCard("TicketPax", "name"),
                    message: `DUPLICATE ${passengerName}`,
                    level: ErrorLevel.Error,
                });
            }
        }
    }

    private static ValidateFlightLegs(ticket: TicketExtended, errors: ValidationResult[]) {
        const upDownRequired = UpDownTimeOnEachLeg(ticket);
        const requiredFields = upDownRequired ? ["location", "up_time", "down_time"] : ["location"];
        const locationsToValidate = DefinitelyAnArray(ticket.Locations).slice();

        this.validateRequiredFieldsArray(locationsToValidate, "TicketLocation", requiredFields, errors);

        if (!upDownRequired) {
            // a simple required check doesn't work for hours, it needs to have a non-zero value
            locationsToValidate.forEach((l) => {
                if (!l.hours || l.hours <= 0) {
                    errors.push({
                        entityId: l.id,
                        entityName: "TicketLocation",
                        propertyName: "hours",
                        editCard: this.getEditCard("TicketLocation", "hours"),
                        message: `HOURS REQUIRED`,
                        level: ErrorLevel.Error,
                    });
                }
            });
        }

        if (ticket.up_down_required) {
            locationsToValidate.sort(sortFlightLegsByTime);

            locationsToValidate.forEach((l) => {
                if (isInvalidTimeString(l.up_time)) {
                    errors.push({
                        entityId: l.id,
                        entityName: "TicketLocation",
                        propertyName: "up_time",
                        editCard: this.getEditCard("TicketLocation", "up_time"),
                        message: "NOT A VALID TIME",
                        level: ErrorLevel.Error,
                    });
                }

                if (isInvalidTimeString(l.down_time)) {
                    errors.push({
                        entityId: l.id,
                        entityName: "TicketLocation",
                        propertyName: "down_time",
                        editCard: this.getEditCard("TicketLocation", "down_time"),
                        message: "NOT A VALID TIME",
                        level: ErrorLevel.Error,
                    });
                }

                if (isValidTimeString(l.up_time) && isValidTimeString(l.down_time)) {
                    const up = dayjs(l.up_time);
                    const down = dayjs(l.down_time);
                    if (down.isSame(up, "minute") || down.isBefore(up)) {
                        errors.push({
                            entityId: l.id,
                            entityName: "TicketLocation",
                            propertyName: "down_time",
                            editCard: this.getEditCard("TicketLocation", "down_time"),
                            message: "DOWN MUST BE AFTER UP",
                            level: ErrorLevel.Error,
                        });
                    }
                }
            });

            // since these are sorted by time, we only need to compare the first n-1 rows to the following row
            if (locationsToValidate.length > 1)
                for (let a = 0; a < locationsToValidate.length - 1; a++) {
                    const b = a + 1;
                    const a_up = isValidTimeString(locationsToValidate[a].up_time) ? dayjs(locationsToValidate[a].up_time) : dayjs(locationsToValidate[a].down_time);
                    const a_down = isValidTimeString(locationsToValidate[a].down_time) ? dayjs(locationsToValidate[a].down_time) : dayjs(locationsToValidate[a].up_time);
                    const b_up = isValidTimeString(locationsToValidate[b].up_time) ? dayjs(locationsToValidate[b].up_time) : dayjs(locationsToValidate[b].down_time);
                    const b_down = isValidTimeString(locationsToValidate[b].down_time) ? dayjs(locationsToValidate[b].down_time) : dayjs(locationsToValidate[b].up_time);

                    const overlap = this.isReadyForOverlapCheck(locationsToValidate[a], locationsToValidate[b]) && a_up.isBefore(b_down) && b_up.isBefore(a_down);

                    if (overlap) {
                        errors.push({
                            entityId: locationsToValidate[a].id,
                            entityName: "TicketLocation",
                            propertyName: HasValue(locationsToValidate[a].down_time) ? "down_time" : "up_time",
                            editCard: this.getEditCard("TicketLocation", "down_time"),
                            message: `FLIGHT TIMES CANNOT OVERLAP`,
                            level: ErrorLevel.Error,
                        });
                        errors.push({
                            entityId: locationsToValidate[b].id,
                            entityName: "TicketLocation",
                            propertyName: HasValue(locationsToValidate[b].up_time) ? "up_time" : "down_time",
                            editCard: this.getEditCard("TicketLocation", "up_time"),
                            message: `FLIGHT TIMES CANNOT OVERLAP`,
                            level: ErrorLevel.Error,
                        });
                    }
                }
        }
    }

    private static isReadyForOverlapCheck(a: TicketLocation, b: TicketLocation) {
        let values = 0;

        values += isValidTimeString(a.up_time) ? 1 : 0;
        values += isValidTimeString(a.down_time) ? 1 : 0;
        values += isValidTimeString(b.up_time) ? 1 : 0;
        values += isValidTimeString(b.down_time) ? 1 : 0;

        return values >= 3;
    }

    private static ValidateFuel(ticket: TicketExtended, errors: ValidationResult[], aircraft: Aircraft | undefined) {
        // we don't know the burn rate until we have an a/c type so skip this check until it is set
        if (aircraft) {
            const fuel = FuelCalculator.Calculate(ticket, aircraft);
            const fuelLineItems = DefinitelyAnArray(ticket.LineItems?.filter((r) => FUEL_LINE_ITEM_TYPES.findIndex((id) => id === r?.line_item_type_id) >= 0));
            if (!fuel.fuelToAllocate.eq(0)) {
                if (fuel.allocatedFuel.gt(0)) {
                    const isReadOnly = this.IsReadOnly(ticket);
                    const commentsHaveBeenEntered =
                        fuelLineItems.findIndex((i) => {
                            return HasValue((i?.comments || "").trim());
                        }) >= 0;

                    // the comments required behaviour was implemented late in the game, did not want to show the comments required error on existing signed (read-only) ticket
                    if (isReadOnly || commentsHaveBeenEntered) {
                        // just warn about the mismatch
                        errors.push({
                            entityId: ticket.id,
                            entityName: "n/a",
                            propertyName: "n/a",
                            editCard: "ticket/fuel",
                            message: `${fuel.fuelToAllocate.toFixed(1)} LITRES OF FUEL EXPECTED`, // TODO units shouldn't be hardcoded
                            level: ErrorLevel.Warn,
                        });
                    } else {
                        errors.push({
                            entityId: fuelLineItems[0].id,
                            entityName: "n/a",
                            propertyName: "comments",
                            editCard: "ticket/fuel",
                            message: `COMMENTS REQUIRED`,
                            level: ErrorLevel.Error,
                        });
                    }
                } else {
                    // If there are no fuel rows at all, show the form-level required message.
                    // If rows exist, row-level litres validation provides a clearer single error.
                    if (fuelLineItems.length === 0) {
                        errors.push({
                            entityId: ticket.id,
                            entityName: "n/a",
                            propertyName: "n/a",
                            editCard: "ticket/fuel",
                            message: `${getDescriptiveName("ticket/fuel")} REQUIRED`,
                            level: ErrorLevel.Error,
                        });
                    }
                }
            }
        }
    }

    private static ValidateLineItems(ticket: TicketExtended, errors: ValidationResult[]) {
        const lineItemsToValidate = DefinitelyAnArray(ticket.LineItems);

        lineItemsToValidate.forEach((lineItem) => {
            if (lineItem.line_item_type_id == null)
                errors.push({
                    entityId: lineItem.id,
                    entityName: "ticket/charges",
                    propertyName: "line_item_type_id",
                    editCard: this.getEditCard("ticket/charges", "line_item_type_id"),
                    message: `${this.getFriendlyName("ticket/charges", "line_item_type_id")} REQUIRED`,
                    level: ErrorLevel.Error,
                });
            if (lineItem.line_item_type_id === DUMMY_CHARGE_LINE_ITEM_TYPE)
                errors.push({
                    entityId: lineItem.id,
                    entityName: "ticket/charges",
                    propertyName: "line_item_type_id",
                    editCard: this.getEditCard("ticket/charges", "line_item_type_id"),
                    message: `${this.getFriendlyName("ticket/charges", "line_item_type_id")} REQUIRED`,
                    level: ErrorLevel.Error,
                });
            if (lineItem.line_item_type_id === DUMMY_FUEL_LINE_ITEM_TYPE)
                errors.push({
                    entityId: lineItem.id,
                    entityName: "FUEL",
                    propertyName: "line_item_type_id",
                    editCard: this.getEditCard("ticket/fuel", "line_item_type_id"),
                    message: `${this.getFriendlyName("ticket/fuel", "line_item_type_id")} REQUIRED`,
                    level: ErrorLevel.Error,
                });
            if (
                FUEL_LINE_ITEM_TYPES.findIndex((id) => id === lineItem.line_item_type_id) >= 0 &&
                lineItem.line_item_type_id !== DUMMY_FUEL_LINE_ITEM_TYPE &&
                (lineItem.amount ?? 0) < 1
            )
                errors.push({
                    entityId: lineItem.id,
                    entityName: "ticket/fuel",
                    propertyName: "amount",
                    editCard: this.getEditCard("ticket/fuel", "amount"),
                    message: `${this.getFriendlyName("ticket/fuel", "amount")} REQUIRED`,
                    level: ErrorLevel.Error,
                });
            if (lineItem.line_item_type_id === LANDING_LINE_ITEM_TYPE && !HasValue(lineItem.comments))
                errors.push({
                    entityId: lineItem.id,
                    entityName: "LANDINGS",
                    propertyName: "comments",
                    editCard: this.getEditCard("LANDINGS", "comments"),
                    message: `${this.getFriendlyName("LANDINGS", "comments")} REQUIRED`,
                    level: ErrorLevel.Error,
                });
            if (lineItem.line_item_type_id === LANDING_LINE_ITEM_TYPE && (lineItem.quantity ?? 0) <= 0)
                errors.push({
                    entityId: lineItem.id,
                    entityName: "LANDINGS",
                    propertyName: "quantity",
                    editCard: this.getEditCard("LANDINGS", "quantity"),
                    message: `${this.getFriendlyName("LANDINGS", "quantity")} REQUIRED`,
                    level: ErrorLevel.Error,
                });
        });

        const passengerCount = ticket.Pax?.length ?? 0;
        const flightLegCount = ticket.Locations?.length ?? 0;
        const fuelCount = FilterLineItems(ticket, FUEL_LINE_ITEM_TYPES).length;
        const landingCount = FilterLineItems(ticket, LANDING_LINE_ITEM_TYPES).length;
        if (ticket.is_no_fly !== 0) {
            if (passengerCount > 0) {
                this.pushLineItemTypeNotAllowedError(errors, "ticket/pax");
            }
            if (flightLegCount > 0) {
                this.pushLineItemTypeNotAllowedError(errors, "ticket/legs");
            }
            if (fuelCount > 0) {
                this.pushLineItemTypeNotAllowedError(errors, "ticket/fuel");
            }
            if (landingCount > 0) {
                this.pushLineItemTypeNotAllowedError(errors, "ticket/landings");
            }
        } else {
            if (flightLegCount === 0) {
                this.pushLineItemTypeRequiredError(errors, "ticket/legs");
            }
        }

        const perDiemCount = FilterLineItems(ticket, PER_DIEMS_LINE_ITEM_TYPES).length;
        if (ticket.is_per_diems) {
            if (perDiemCount === 0) {
                this.pushLineItemTypeRequiredError(errors, "ticket/perdiems");
            }
        } else {
            if (perDiemCount ?? 0 > 0) {
                this.pushLineItemTypeNotAllowedError(errors, "ticket/perdiems");
            }
        }

        const otherChargesCount = FilterLineItems(ticket, OTHER_CHARGES_LINE_ITEM_TYPES).length;
        if (ticket.is_other_charges) {
            if (otherChargesCount === 0) {
                this.pushLineItemTypeRequiredError(errors, "ticket/charges");
            }
        } else {
            if (otherChargesCount > 0) {
                this.pushLineItemTypeNotAllowedError(errors, "ticket/charges");
            }
        }

        const landingsCount = FilterLineItems(ticket, LANDING_LINE_ITEM_TYPES).length;
        if (ticket.any_landings) {
            if (landingsCount === 0) {
                this.pushLineItemTypeRequiredError(errors, "ticket/landings");
            }
        } else {
            if (landingsCount > 0) {
                this.pushLineItemTypeNotAllowedError(errors, "ticket/landings");
            }
        }
    }

    private static pushLineItemTypeRequiredError(errors: ValidationResult[], formName: string) {
        errors.push({
            entityId: 0,
            entityName: formName,
            propertyName: "not_applicable",
            editCard: formName,
            message: `${getDescriptiveName(formName)} REQUIRED`,
            level: ErrorLevel.Error,
        });
    }

    private static pushLineItemTypeNotAllowedError(errors: ValidationResult[], formName: string) {
        errors.push({
            entityId: 0,
            entityName: formName,
            propertyName: "not_applicable",
            editCard: formName,
            message: `${getDescriptiveName(formName)} NOT ALLOWED`,
            level: ErrorLevel.Error,
        });
    }

    private static validateFirstUpLastDown(ticket: TicketExtended, errors: ValidationResult[]) {
        if (isInvalidTimeString(ticket.up_time)) {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: ticket.id,
                entityName: "Ticket",
                propertyName: "up_time",
                editCard: this.getEditCard("Ticket", "up_time"),
                message: `NOT A VALID TIME`,
                level: ErrorLevel.Error,
            });
        }

        if (isInvalidTimeString(ticket.down_time)) {
            errors.push({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                entityId: ticket.id,
                entityName: "Ticket",
                propertyName: "down_time",
                editCard: this.getEditCard("Ticket", "down_time"),
                message: `NOT A VALID TIME`,
                level: ErrorLevel.Error,
            });
        }
        if (isValidTimeString(ticket.up_time) && isValidTimeString(ticket.down_time)) {
            if (dayjs(ticket.down_time).isBefore(dayjs(ticket.up_time).add(4, "minutes"))) {
                errors.push({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    entityId: ticket.id,
                    entityName: "Ticket",
                    propertyName: "down_time",
                    editCard: this.getEditCard("Ticket", "down_time"),
                    message: `DOWN MUST BE AFTER UP`,
                    level: ErrorLevel.Error,
                });
            }

            const flightTime = TimeCalculator.getTotalFlightTime(ticket).toNumber();
            const elapsedTime = TimeCalculator.getDifferenceInHours(ticket.up_time, ticket.down_time);
            if (flightTime > elapsedTime) {
                errors.push({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    entityId: ticket.id,
                    entityName: "Ticket",
                    propertyName: "down_time",
                    editCard: this.getEditCard("Ticket", "down_time"),
                    message: `ELAPSED TIME (${elapsedTime.toFixed(1)} HOURS) CANNOT BE LESS THAN TOTAL FLIGHT HOURS`,
                    level: ErrorLevel.Error,
                });
            }
        }
    }
}
